'use client'

import { TeamDetailsData } from '@dc-shared-web/hooks/api/team'
import { User } from '@dc-shared-web/hooks/api/user'
import { useHighSecurityTeamWarning } from '@/app/core/hooks/useHighSecurityTeamWarning'
import { BaseAlert, BaseDialog, Button } from '@/dc-shared-web/src'
import { faShieldHalved } from '@document-crunch/pro-regular-svg-icons'

type HighSecurityTeamWarningDialogProps = {
  team: TeamDetailsData | undefined
  user: User
}

/**
 * Dialog component that displays a privacy warning when Document Crunch employees
 * access teams with high_security entitlement enabled.
 *
 * The warning appears once per team per browser session and requires acknowledgment
 * to proceed.
 */
export const HighSecurityTeamWarningDialog = ({
  team,
  user,
}: HighSecurityTeamWarningDialogProps) => {
  const { shouldShowWarning, handleDismiss } = useHighSecurityTeamWarning(
    team,
    user
  )

  if (!shouldShowWarning) {
    return null
  }

  return (
    <BaseDialog
      isOpen
      onClose={() => {}} // Prevent closing without acknowledgment
      title='Privacy-protected team'
      primaryActionSlot={
        <Button
          onClick={handleDismiss}
          variant='default'
          data-testid='dismiss-warning-button'
        >
          I understand
        </Button>
      }
      secondaryActionSlot={<></>}
      size='medium'
      disableOutsideClick
      disableEscapeClose
      data-testid='high-security-warning-dialog'
    >
      <BaseAlert
        variant='warning'
        icon={faShieldHalved}
        title={`Concerning ${team?.name}`}
        description={
          <div className='tw-space-y-2'>
            <p>
              This customer has restricted data sharing with Document Crunch.
            </p>
            <p>
              Please be mindful when accessing or reviewing their data, and
              ensure compliance with their privacy requirements.
            </p>
          </div>
        }
      />
    </BaseDialog>
  )
}
